import { unit } from 'mathjs';
import TransformDistance from './TransformDistance';
import { TRAIN_MODE, REST_MODE, WARM_UP_MODE, RELAX_MODE, SPORT_TYPE } from './constant';

import { getGmapConstructor } from '../../utils/get-gmap';
import { convertToAMapCoordinate, getGodvMapConstructor } from '../../utils/get-godv';
import { checkIfIsSkiing, getMarkerContent } from './utils/marker';

export class CalcMarkers {
  constructor(basisMarkerConfig, unit) {
    this.next = null;
    this.basisMarkerConfig = basisMarkerConfig;
    this.unit = unit;
    this._defaultField4markerText = 'lapIndex';
  }

  parseValue(src) {
    if (src === undefined) {
      return '--';
    }

    return src;
  }

  async generateMarker(isGoogleMap, gps, content) {
    if (isGoogleMap) {
      const GmapClass = await getGmapConstructor();
      return new GmapClass.Marker({
        zIndex: 60,
        position: {
          lng: gps[0],
          lat: gps[1]
        },
        optimized: true,
        label: content,
        // 透明1px 背景
        icon: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
      });
    } else {
      const GodvMap = await getGodvMapConstructor();

      const [position] = await convertToAMapCoordinate(gps);
      return new GodvMap.Marker({
        position,
        content,
        ...this.basisMarkerConfig
      });
    }
  }

  setNext(fn) {
    this.next = fn;
  }
}

/**
 * 没有有效圈数据的话，就不展示圈标记
 */
export class NoMarker extends CalcMarkers {
  exec(laps, summary) {
    if (laps.length === 0) return Promise.resolve([]);

    return this.next.exec(...arguments);
  }
}

/**
 * 雪场单板和双板圈标记点计算
 */
export class CalcSkiBoardAndSnowBoardMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    if (checkIfIsSkiing(summary.sportType)) {
      return Promise.all(
        laps.map((lapInfo) => {
          return this.generateMarker(isGoogleMap, lapInfo._markerGps, getMarkerContent('theme-dark', lapInfo.lapIndex, isGoogleMap));
        })
      );
    }

    return this.next.exec(...arguments);
  }
}

/**
 * 间歇运动圈标记点计算
 */
export class CalcIntervalTrainingMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    if (summary.sportMode === 1) {
      return Promise.all(
        laps.map((lapInfo) => {
          const map4markerText = {
            // x_activityIndex 在 ./MarkLap.js 中处理
            [TRAIN_MODE]: `${lapInfo.x_activityIndex}T`,
            [REST_MODE]: 'R',
            [WARM_UP_MODE]: `${lapInfo.x_activityIndex}W`,
            [RELAX_MODE]: 'R'
          };
          return this.generateMarker(
            isGoogleMap,
            lapInfo._markerGps,
            getMarkerContent('theme-dark', map4markerText[lapInfo.mode] || lapInfo[this._defaultField4markerText], isGoogleMap)
          );
        })
      );
    }

    return this.next.exec(...arguments);
  }
}

/**
 * 攀登运动圈标记点计算
 */
export class CalcClimbMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    if (summary.sportType === SPORT_TYPE.CLIMB) {
      return Promise.all(
        laps.map((lapInfo) => {
          const rawDistance = unit(lapInfo.elevGain, 'm');
          // 距离格式化只保留整数
          const content = Number(TransformDistance.transform(rawDistance, summary.sportType, summary, this.unit).toFixed(0));
          return this.generateMarker(isGoogleMap, lapInfo._markerGps, getMarkerContent('theme-dark', content, isGoogleMap));
        })
      );
    }

    return this.next.exec(...arguments);
  }
}

/**
 * 登山滑雪运动圈标记点计算
 */
export class CalcMountainSkiingMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    if (summary.sportType === SPORT_TYPE.MOUNTAIN_SKIING) {
      let currentLapIndex = 1;
      let count = 0;
      const map4direction = {
        3: '↑',
        4: '↓'
      };
      return Promise.all(
        laps.map((lapInfo) => {
          const content = `${currentLapIndex}${this.parseValue(map4direction[lapInfo.lapType])}`;
          count++;
          if (count === 2) {
            count = 0;
            currentLapIndex++;
          }
          return this.generateMarker(isGoogleMap, lapInfo._markerGps, getMarkerContent('theme-dark', content, isGoogleMap));
        })
      );
    }

    return this.next.exec(...arguments);
  }
}

/**
 * 训练组合圈标记点计算
 */
export class CalcCombinationTrainingMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    if (summary.programId > 0) {
      /**
       * 热身 Warm up  -> W
       * 训练 Training   -> T
       * 恢复 Rest   -> R
       * 放松 Cool Down  -> C
       */
      const map4contentSuffix = {
        [TRAIN_MODE]: 'T',
        [REST_MODE]: 'R',
        [WARM_UP_MODE]: 'W',
        [RELAX_MODE]: 'C'
      };
      // console.log('CalcCombinationTrainingMarkers');
      return Promise.all(
        laps.map((lapInfo) => {
          // 格式有三种：-y z/x/xz
          // x 为exerciseIndex，y为setIndex，z为运动类型
          const content = `${lapInfo.x_activityIndex || ''}${map4contentSuffix[lapInfo.mode] || ''}`;
          // console.log(`lapInfo.x_activityIndex`, lapInfo.mode, lapInfo.x_activityIndex, content);
          return this.generateMarker(isGoogleMap, lapInfo._markerGps, getMarkerContent('theme-dark', content, isGoogleMap));
        })
      );
    }

    return this.next.exec(...arguments);
  }
}

/**
 * 普通运动圈标记点计算
 */
export class CalcNormalTrainingMarkers extends CalcMarkers {
  exec(laps, summary, isGoogleMap) {
    return Promise.all(
      laps.map((lapInfo) => {
        const rawDistance = unit(lapInfo.totalDistance, 'cm');
        // 距离格式化最多两位小数点，不补零
        const content = Number(TransformDistance.transform(rawDistance, summary.sportType, summary, this.unit).toFixed(2));
        return this.generateMarker(isGoogleMap, lapInfo._markerGps, getMarkerContent('theme-dark', content, isGoogleMap));
      })
    );
  }
}
